import styles from "./Layout.module.css";
import { Dialog, Stack, TextField, Image } from "@fluentui/react";
import arrow_icon from "../../../../assets/arrow.svg";
import { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import {
  fecthCloseTicket,
  fetchConversationLink,
  fetchHelpWeb,
  getBotSetup,
} from "../../../../api/widget.api";
import EventContext from "../../../../EventContext";
import React from "react";
import {
  SystemMessageProvider,
  useSystemMessage,
} from "../../../SystemMessage/SystemMessageContext";
import "../../../../index.css";
import { useHelp, HelpProvider } from "../../../HelpContext/helpContext";

interface LayoutProps {
  children?: React.ReactNode;
  botSettings?: any;
}

const Layout: React.FC<LayoutProps> = ({ children, botSettings }) => {
  const abortFuncs = useRef([] as AbortController[]);
  const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [mainColor, setMainColor] = useState<string | undefined>(undefined);
  const [kustTitle, setKustTitle] = useState<string | undefined>("Kust Agent");
  const [logo, setLogo] = useState<string | undefined>(undefined);

  const { setSystemMessage } = useSystemMessage();
  const {
    setHelpMode,
    isHelpDisabled,
    setHelpDisabled,
    isHelpShowed,
    setHelpShowed,
  } = useHelp();
  const [token, setToken] = useState("");

  const onLoadSession = () => {
    const helpMode = sessionStorage.getItem("helpMode");
    if (helpMode) {
      const helped: boolean = JSON.parse(helpMode).help;
      setHelpDisabled(helped);
      setHelpShowed(helped);
      const closeDisable: boolean = JSON.parse(helpMode).role != "support";
    }
  };
  const { closeTriggered } = useContext(EventContext);

  React.useEffect(() => {
    // if (closeTriggered) {
    setHelpDisabled(false);
    setHelpShowed(true);

    // }
  }, [closeTriggered]);

  const { triggerEvent, eventTriggered } = useContext(EventContext);

  React.useEffect(() => {
    if (eventTriggered) {
      setHelpDisabled(true);
      setHelpShowed(true);
      setHelpMode({ help: true, role: "user" });
      setSystemMessage("Your request for help has been sent to Admin.");
    }
  }, [eventTriggered]);

  const onClickHelp = async () => {
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    try {
      const help = await fetchHelpWeb(
        abortController.signal,
        sessionStorage.getItem("accessToken") as string
      );
      setHelpMode({ help: true, role: "user" });
      onLoadSession();
      triggerEvent();
      setHelpDisabled(true);
      setToken(help.token);
      setSystemMessage("Your request for help has been sent to Admin.");
    } catch (e) {
      console.log(e);
    } finally {
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
    }
  };

  const setupBot = async () => {
    setLoading(true);
    try {
      // Set a timeout to prevent loading indefinitely if the request takes too long
      if (botSettings) {
        if (botSettings.themeColor) setMainColor(botSettings.themeColor);
        if (botSettings.kustTitle) setKustTitle(botSettings.kustTitle);
        if (botSettings.logo) setLogo(botSettings.logo);
      } else {
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        const setup = await getBotSetup(abortController.signal, sessionStorage.getItem("accessToken") as string);
        if (setup.themeColor) setMainColor(setup.themeColor);
        if (setup.kustTitle) setKustTitle(setup.kustTitle);
        if (setup.logo) setLogo(setup.logo);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  const isLightColor = (color: string) => {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 186;
  };

  useEffect(() => {
    setHelpDisabled(false);
    setToken(sessionStorage.getItem("accessToken") as string);
    onLoadSession();
    setupBot();
  }, []);

  const closeChat = () => {
    if (window && (window as any).ChatWidget && (window as any).ChatWidget.close) {
      (window as any).ChatWidget.close(".kust-chat-widget-container", ".kust-chat-widget-button");
    } else {
      // Fallback: remove open class directly if present
      const chatContainer = document.querySelector('.kust-chat-widget-container') as HTMLElement | null;
      if (chatContainer) {
        chatContainer.classList.remove('open');
        // also hide if style logic depends on display
        // chatContainer.style.display = 'none';
      }
    }
  }

  return (
    <HelpProvider>
      <SystemMessageProvider>
        {isLoading ? (
          <div className={styles.layout} style={{ backgroundColor: mainColor || "#000", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <div style={{width: '4px', height: '4px', borderRadius: '50%', background: '#fff', animation: 'bounce 1s infinite'}}></div>
              <div style={{width: '4px', height: '4px', borderRadius: '50%', background: '#fff', animation: 'bounce 1s infinite .2s'}}></div>
              <div style={{width: '4px', height: '4px', borderRadius: '50%', background: '#fff', animation: 'bounce 1s infinite .4s'}}></div>
              <style>{`
                @keyframes bounce {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-6px); }
                }
              `}</style>
            </div>
          </div>
        ) : (
          <div className={styles.layout} style={{ backgroundColor: '#f8f8f8' }}>
            <header className={styles.header} role="banner">
              <div className={styles.headerContainer} style={{ backgroundColor: '#f8f8f8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  
                  {logo && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', overflow: 'hidden', borderRadius: '50%' }}>
                      {/* Preload the logo image */}
                      <link rel="preload" as="image" href={logo} />
                      <Image
                        src={logo}
                        alt="Logo"
                        shouldFadeIn={false}
                        shouldStartVisible={true}
                        width={26}
                        height={26}
                        loading="eager"
                      />
                    </div>
                  )}
                  <span style={{ 
                    color: '#333',
                    fontWeight: 600,
                    marginLeft: '1px',
                  }}>
                    {kustTitle}
                  </span>
                  <div style={{ marginLeft: 'auto' }}>
                    {/* <button 
                      className={styles.chatToggleButton} 
                      onClick={() => closeChat()}
                      style={{
                        width: '35px',
                        height: '35px',
                        borderRadius: '50%',
                        display: 'flex',
                        border: '2px solid #b5b5b5',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    > */}
                      <Image 
                        onClick={() => closeChat()}
                        src={arrow_icon} 
                        alt="Close Chat" 
                        className={styles.chatToggleIconHeader}
                      />
                    {/* </button> */}
                  </div>
                </div>

              </div>
            </header>
            {children}
          </div>
        )}
      </SystemMessageProvider>
    </HelpProvider>
  );
};

export default Layout;
